import {
  lazy,
  PropsWithChildren,
  Suspense,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { DialogContext, DialogTypeArray } from '../../shared/context';
import type { DialogType, WithOptionalProps } from '../../shared/context';

const ConnectWalletDialog = lazy(() =>
  import('../../entities/wallets/ConnectWalletDialog').then((module) => ({
    default: module.ConnectWalletDialog,
  })),
);

const UnsupportedNetworkDialog = lazy(() =>
  import('../../entities/governance/UnsupportedNetworkDialog').then(
    (module) => ({
      default: module.UnsupportedNetworkDialog,
    }),
  ),
);

const LinkTelegramDialog = lazy(() =>
  import('../../entities/governance/LinkTelegramDialog').then((module) => ({
    default: module.LinkTelegramDialog,
  })),
);

type DialogState = {
  [key in DialogType]?: {
    props: WithOptionalProps<key>;
  };
};

// TODO: move to ui lib
export function DialogProvider({ children }: PropsWithChildren): JSX.Element {
  const [openDialogs, setOpenDialogs] = useState<DialogState>({});

  const openDialog = useCallback(
    <T extends DialogType>(dialogType: T, props?: WithOptionalProps<T>) => {
      setOpenDialogs((prevDialogs) => ({
        ...prevDialogs,
        [dialogType]: { props },
      }));
    },
    [],
  );

  const closeDialog = useCallback((dialogType: DialogType) => {
    setOpenDialogs((prevDialogs) => {
      const { [dialogType]: _, ...remainingDialogs } = prevDialogs;
      return remainingDialogs;
    });
  }, []);

  const closeAllDialogs = useCallback(() => setOpenDialogs({}), []);

  const memoedValue = useMemo(
    () => ({
      openDialog,
      closeDialog,
      closeAllDialogs,
    }),
    [openDialog, closeDialog, closeAllDialogs],
  );

  const isOpen = (currentDialog: DialogType): boolean =>
    Object.entries(openDialogs).some(([dialog]) => dialog === currentDialog);

  return (
    <DialogContext.Provider value={memoedValue}>
      {children}
      <Suspense fallback={<div>Loading...</div>}>
        {Object.entries(openDialogs).map(([type, { props }]) => {
          const isTypeCorrect = (key: string): key is DialogType =>
            DialogTypeArray.some((dialogType) => dialogType === type);

          if (!isTypeCorrect(type)) return null;

          const isCurrentDialogOpen = isOpen(type);
          const baseDialogProps = {
            open: isCurrentDialogOpen,
            onOpenChange: () => closeDialog(type),
          };

          switch (type) {
            case 'ConnectWallet':
              return (
                <ConnectWalletDialog
                  key={type}
                  {...baseDialogProps}
                  {...props}
                />
              );
            case 'LinkTelegram':
              return (
                <LinkTelegramDialog
                  key={type}
                  {...baseDialogProps}
                  {...props}
                />
              );
            case 'UnsupportedNetwork':
              return (
                <UnsupportedNetworkDialog
                  key={type}
                  {...baseDialogProps}
                  {...props}
                />
              );
            default:
              return null;
          }
        })}
      </Suspense>
    </DialogContext.Provider>
  );
}
